/**
 * Onboarding State Management
 * Generic system for handling user onboarding flows with flexible state preservation
 */

import { getSetupPath } from '@/lib/setupFeatures';

export interface OnboardingIntent {
  type: 'plugin_install' | 'funnel_create' | 'store_create' | 'custom';
  pluginId?: string;
  pluginName?: string;
  deploymentId?: string; // Version/deployment to install
  preset?: string; // Config preset to use
  funnelType?: string;
  storeId?: string;
  customData?: Record<string, any>;
  timestamp: number;
  source?: string; // 'marketplace', 'direct_link', etc.
}

export interface OnboardingState {
  intent: OnboardingIntent | null;
  currentStep: string; // 'signup', 'create_org', 'create_store', 'complete'
  completedSteps: string[];
  targetUrl?: string; // Final destination after onboarding
}

const ONBOARDING_STORAGE_KEY = 'tagada_onboarding_state';
const INTENT_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Save onboarding intent (e.g., plugin installation from marketplace)
 */
export function saveOnboardingIntent(intent: Omit<OnboardingIntent, 'timestamp'>): void {
  try {
    const state: OnboardingState = {
      intent: {
        ...intent,
        timestamp: Date.now(),
      },
      currentStep: 'signup',
      completedSteps: [],
    };

    localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(state));

    // Analytics
    if ((window as any).analytics) {
      (window as any).analytics.track('Onboarding Intent Saved', {
        type: intent.type,
        pluginId: intent.pluginId,
        pluginName: intent.pluginName,
        source: intent.source,
      });
    }
  } catch (error) {
    console.error('[ONBOARDING] Failed to save intent:', error);
  }
}

/**
 * Get current onboarding state
 */
export function getOnboardingState(): OnboardingState | null {
  try {
    const stored = localStorage.getItem(ONBOARDING_STORAGE_KEY);
    if (!stored) return null;

    const state: OnboardingState = JSON.parse(stored);

    // Check if intent has expired
    if (state.intent && Date.now() - state.intent.timestamp > INTENT_EXPIRY_MS) {
      console.log('[ONBOARDING] Intent expired, clearing state');
      clearOnboardingState();
      return null;
    }

    return state;
  } catch (error) {
    console.error('[ONBOARDING] Failed to get state:', error);
    return null;
  }
}

/**
 * Update onboarding step
 */
export function updateOnboardingStep(step: string): void {
  try {
    const state = getOnboardingState();
    if (!state) return;

    // Mark previous step as completed
    if (state.currentStep && !state.completedSteps.includes(state.currentStep)) {
      state.completedSteps.push(state.currentStep);
    }

    state.currentStep = step;
    localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(state));

    // Analytics
    if ((window as any).analytics) {
      (window as any).analytics.track('Onboarding Step Updated', {
        step,
        intent: state.intent?.type,
        completedSteps: state.completedSteps,
      });
    }
  } catch (error) {
    console.error('[ONBOARDING] Failed to update step:', error);
  }
}

/**
 * Set target URL for completion
 */
export function setOnboardingTargetUrl(url: string): void {
  try {
    const state = getOnboardingState();
    if (!state) return;

    state.targetUrl = url;
    localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('[ONBOARDING] Failed to set target URL:', error);
  }
}

/**
 * Clear onboarding state
 */
export function clearOnboardingState(): void {
  try {
    localStorage.removeItem(ONBOARDING_STORAGE_KEY);

    // Also clear old install_intent for backward compatibility
    localStorage.removeItem('install_intent');
  } catch (error) {
    console.error('[ONBOARDING] Failed to clear state:', error);
  }
}

/**
 * Check if user needs to complete onboarding
 */
export function hasOnboardingIntent(): boolean {
  const state = getOnboardingState();
  return state !== null && state.intent !== null;
}

/**
 * Get next onboarding step based on current state
 */
export function getNextOnboardingStep(params: {
  hasOrganization: boolean;
  hasStore: boolean;
  state: OnboardingState;
}): string | null {
  const { hasOrganization, hasStore, state } = params;

  // If no intent, no next step
  if (!state.intent) return null;

  // Based on intent type and current resources
  switch (state.intent.type) {
    case 'plugin_install':
      if (!hasOrganization) return 'create_org';
      if (!hasStore) return 'create_store';
      return 'install_plugin';

    case 'funnel_create':
      if (!hasOrganization) return 'create_org';
      if (!hasStore) return 'create_store';
      return 'create_funnel';

    case 'store_create':
      if (!hasOrganization) return 'create_org';
      return 'create_store';

    case 'custom':
      // Custom logic can be handled by the caller
      return null;

    default:
      return null;
  }
}

/**
 * Build redirect URL based on intent
 */
export function buildOnboardingRedirectUrl(params: {
  orgSlug: string;
  storeId?: string;
  state: OnboardingState;
}): string {
  const { orgSlug, storeId, state } = params;

  // If target URL is explicitly set, use it
  if (state.targetUrl) {
    return state.targetUrl;
  }

  // Build URL based on intent
  switch (state.intent?.type) {
    case 'plugin_install':
      if (storeId) {
        // Redirect to plugins page with installation modal and all parameters
        const params = new URLSearchParams();
        params.set('install', state.intent.pluginId || '');
        if (state.intent.deploymentId) {
          params.set('deployment', state.intent.deploymentId);
        }
        if (state.intent.preset) {
          params.set('preset', state.intent.preset);
        }
        return `/org/${orgSlug}/stores/${storeId}/plugins?${params.toString()}`;
      }
      // No store yet, will redirect after store creation
      return `/org/${orgSlug}/stores`;

    case 'funnel_create':
      if (storeId) {
        return `/org/${orgSlug}/stores/${storeId}/storefront/marketplace`;
      }
      return `/org/${orgSlug}/stores`;

    case 'store_create':
      if (storeId) {
        return `/org/${orgSlug}/stores/${storeId}/general/products`;
      }
      return getSetupPath(orgSlug);

    default:
      return `/org/${orgSlug}`;
  }
}

/**
 * Complete onboarding and redirect
 */
export function completeOnboarding(params: {
  orgSlug: string;
  storeId?: string;
}): string {
  const { orgSlug, storeId } = params;
  const state = getOnboardingState();

  if (!state) {
    return `/org/${orgSlug}`;
  }

  const redirectUrl = buildOnboardingRedirectUrl({ orgSlug, storeId, state });

  // Analytics
  if ((window as any).analytics) {
    (window as any).analytics.track('Onboarding Completed', {
      intent: state.intent?.type,
      completedSteps: state.completedSteps,
      redirectUrl,
      duration: state.intent ? Date.now() - state.intent.timestamp : 0,
    });
  }

  // Clear state (and any stale skip marker — a completed setup supersedes a skip).
  clearOnboardingState();
  clearOnboardingSkipped(orgSlug);

  return redirectUrl;
}

/**
 * "Skipped guided setup" marker — persisted per org so the choice survives reloads and
 * reconnects (same browser). The GuidedSetupGuard sends every store-less merchant to
 * /setup; this flag is the explicit opt-out that tells the guard to leave them on the
 * dashboard instead. Per-org so skipping one org doesn't suppress onboarding for another.
 *
 * Note: localStorage is per-browser/device. A merchant who skipped on one browser and has
 * no store yet will still see setup on a different device — to persist across devices the
 * flag would need to move server-side.
 */
const ONBOARDING_SKIPPED_KEY_PREFIX = 'tagada_onboarding_skipped';

function onboardingSkippedKey(orgSlug: string): string {
  return `${ONBOARDING_SKIPPED_KEY_PREFIX}:${orgSlug}`;
}

export function markOnboardingSkipped(orgSlug: string): void {
  try {
    localStorage.setItem(onboardingSkippedKey(orgSlug), new Date().toISOString());
  } catch (error) {
    console.error('[ONBOARDING] Failed to mark setup skipped:', error);
  }
}

export function hasSkippedOnboarding(orgSlug: string): boolean {
  try {
    return localStorage.getItem(onboardingSkippedKey(orgSlug)) !== null;
  } catch (error) {
    console.error('[ONBOARDING] Failed to read setup skipped flag:', error);
    return false;
  }
}

export function clearOnboardingSkipped(orgSlug: string): void {
  try {
    localStorage.removeItem(onboardingSkippedKey(orgSlug));
  } catch (error) {
    console.error('[ONBOARDING] Failed to clear setup skipped flag:', error);
  }
}

/**
 * Skip onboarding without completing it.
 *
 * Persists a per-org "skipped" marker so the GuidedSetupGuard stops forcing a store-less
 * merchant back to /setup on later visits and reconnects, clears any leftover signup
 * intent, and returns the org dashboard route. Anything already created during setup
 * (store, products, shipping, payment, checkout) is persisted server-side — skipping only
 * exits the guided wizard, no data is lost. Setup stays reachable manually at /setup.
 */
export function skipOnboarding(params: { orgSlug: string }): string {
  const { orgSlug } = params;
  const state = getOnboardingState();

  // Analytics
  if ((window as any).analytics) {
    (window as any).analytics.track('Onboarding Skipped', {
      intent: state?.intent?.type ?? null,
      completedSteps: state?.completedSteps ?? [],
    });
  }

  // Remember the explicit choice (survives reconnect) and drop the leftover signup intent.
  markOnboardingSkipped(orgSlug);
  clearOnboardingState();

  return `/org/${orgSlug}`;
}

/**
 * Migrate old install_intent to new onboarding system
 */
export function migrateOldInstallIntent(): void {
  try {
    // Check if new system already has data
    if (getOnboardingState()) return;

    // Check for old install_intent
    const oldIntent = localStorage.getItem('install_intent');
    if (!oldIntent) return;

    const parsed = JSON.parse(oldIntent);

    // Convert to new format
    saveOnboardingIntent({
      type: 'plugin_install',
      pluginId: parsed.pluginId,
      pluginName: parsed.pluginName,
      deploymentId: parsed.deploymentId,
      source: 'marketplace',
    });

    // Remove old key
    localStorage.removeItem('install_intent');

    console.log('[ONBOARDING] Migrated old install_intent to new system');
  } catch (error) {
    console.error('[ONBOARDING] Failed to migrate old intent:', error);
  }
}
